import * as React from 'react';
import {Suspense, useEffect} from 'react';
import {presignedUrlCache} from './util/PresignedUrlCache';

import {createBrowserRouter, Navigate, RouterProvider} from 'react-router-dom';
import {Amplify} from "aws-amplify";
import {HelmetProvider} from "react-helmet-async";
import {ToastProvider} from "./components/ui/toast/ToastContext";
import {UserProvider} from "./context/UserContext";

// Core components that need to stay - layout and error handling
import IndexPage from './pages/Index';
import LoadingPage from './pages/common/LoadingPage';
import RootLayout from "./pages/Root";
import Error404 from "./pages/common/error/Error404";
import AdminRootLayout from "./pages/admin/AdminRoot";
import FunnelRootLayout from "./pages/funnel/FunnelRoot";
import RedirectComponent from "./components/common/RedirectComponent";
import ProductsHeader from "./components/user/products/ProductsHeader";
import ProductsFooter from "./components/user/products/ProductsFooter";

// Referral pages
import ReferralsPage, {loader as referralsLoader} from "./pages/user/products/ReferralsPage";
import ReferralKedPage from "./pages/referral/ReferralKedPage";
import ReferralPlatformPage from "./pages/referral/ReferralPlatformPage";

// Exercise, statistics, and rankings pages are now lazy loaded
// Lazy component imports
import * as FunnelComponents from './components/lazy/FunnelComponents';
import * as AuthComponents from './components/lazy/AuthComponents';
import * as ContentComponents from './components/lazy/ContentComponents';
import * as AdminComponents from './components/lazy/AdminComponents';
import * as ProductComponents from './components/lazy/ProductComponents';

// Action imports are now lazy loaded to reduce bundle size
// Loader imports (kept as they're needed for dynamic loading)
import * as funnelLoaders from "./loaders/funnelLoaders";
import * as authLoaders from "./loaders/authLoaders";
import * as contentLoaders from "./loaders/contentLoaders";

// Utility imports
import {
    redirectIfLoggedIn,
    redirectIfNotAdmin,
    redirectIfNotFullAdmin,
    redirectIfNotLoggedIn,
    tokenLoader
} from "./util/AuthUtil";
import {AvailableCourses} from "./model/user/learning-course/AvailableCourses";
import {CourseDetails} from "./model/user/learning-course/CourseDetails";

// Lazy loaders for admin, product pages, and actions
import * as adminLoaders from "./loaders/adminLoaders";
import * as productLoaders from "./loaders/productLoaders";
import * as lazyActions from "./loaders/lazyActions";
import ChunkErrorBoundary from "./components/common/error/ChunkErrorBoundary";
import {TraceService} from "./service/TraceService";
import {TraceType} from "./model/user/general/Trace";
import {DEFAULT_EXPERIMENT, LAUNCH_LANDING_EXPERIMENT, SMS_CONTENT_EXPERIMENT} from "./util/ExperimentUtil";

// No need for individual lazy imports - they're all in the component modules now

// Helper function to wrap lazy components with Suspense
const withSuspense = (Component: React.ComponentType<any>) => {
    return (props: any = {}) => (
        <Suspense fallback={<LoadingPage minHeight='100vh' styles='pt-16' useGradientBackground={true}/>}>
            <Component {...props} />
        </Suspense>
    );
};

// Helper function for product pages with gradient background
const withSuspenseGradient = (Component: React.ComponentType<any>) => {
    return (props: any = {}) => (
        <Suspense fallback={<LoadingPage minHeight='100vh' styles='pt-16' useGradientBackground={true}/>}>
            <Component {...props} />
        </Suspense>
    );
};

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: process.env.REACT_APP_USER_POOL_ID!!,
            userPoolClientId: process.env.REACT_APP_USER_POOL_APP_CLIENT_ID!!,
            loginWith: {
                oauth: {
                    domain: `${process.env.REACT_APP_ENV}-auth-produkacja.auth.eu-central-1.amazoncognito.com`,
                    scopes: ['email', 'profile', 'openid'],
                    responseType: 'code',
                    redirectSignIn: process.env.REACT_APP_REDIRECT_SIGN_IN_COMMA_SEPARATED!!.split(','),
                    redirectSignOut: process.env.REACT_APP_REDIRECT_SIGN_OUT_COMMA_SEPARATED!!.split(',')
                }
            }
        }
    }
})

const router = createBrowserRouter([
    {
        path: '/admin',
        element: <AdminRootLayout/>,
        errorElement: <Error404/>,
        loader: redirectIfNotAdmin,
        id: 'adminRoot',
        children: [
            {
                index: true,
                element: withSuspense(AdminComponents.AdminIndex)()
            },
            {
                path: 'metrics',
                element: withSuspense(AdminComponents.AdminMetrics)(),
                loader: adminLoaders.adminMetricsLoader
            },
            {
                path: 'ai',
                element: withSuspense(AdminComponents.AdminAIPage)(),
                loader: adminLoaders.adminAIModelsLoader,
                action: adminLoaders.adminAIAction
            },
            {
                path: 'newsletter',
                element: withSuspense(AdminComponents.AdminSendNewsletterPage)(),
                loader: adminLoaders.adminSendNewsletterLoader,
                action: adminLoaders.adminSendNewsletterAction
            },
            {
                path: 'newsletter/zaplanowane',
                element: withSuspense(AdminComponents.AdminScheduledNewslettersPage)(),
                loader: adminLoaders.adminScheduledNewslettersLoader
            },
            {
                path: 'newsletter/lista',
                element: withSuspense(AdminComponents.AdminListNewslettersPage)(),
                loader: adminLoaders.adminListNewslettersLoader,
                action: adminLoaders.adminToggleNewsletterAction
            },
            {
                path: 'newsletter/dodaj',
                element: withSuspense(AdminComponents.AdminAddToNewsletterPage)(),
                action: adminLoaders.adminAddToNewsletterAction
            },
            {
                path: 'newsletter/statystyki/kampanie',
                element: withSuspense(AdminComponents.AdminNewsletterCampaignsStatisticsPage)(),
                loader: adminLoaders.campaignsLoader
            },
            {
                path: 'newsletter/statystyki/followups',
                element: withSuspense(AdminComponents.AdminNewsletterFollowUpsStatisticsPage)(),
                loader: adminLoaders.followUpsLoader
            },
            {
                path: 'newsletter/statystyki/sms',
                element: withSuspense(AdminComponents.AdminSmsCampaignsStatisticsPage)(),
                loader: adminLoaders.smsCampaignsLoader
            },
            {
                path: 'newsletter/listy',
                element: withSuspense(AdminComponents.AdminMailingListsPage)(),
                loader: adminLoaders.adminMailingListsLoader
            },
            {
                path: 'newsletter/tagi',
                element: withSuspense(AdminComponents.AdminTagsPage)(),
                loader: adminLoaders.adminTagsLoader
            },
            {
                path: 'newsletter/segmenty',
                element: withSuspense(AdminComponents.AdminSegmentsPage)(),
                loader: adminLoaders.adminSegmentsLoader
            },
            {
                path: 'newsletter/automatyzacje',
                element: withSuspense(AdminComponents.AdminAutomationsPage)(),
                loader: adminLoaders.adminAutomationsLoader
            },
            {
                path: 'newsletter/szablony',
                element: withSuspense(AdminComponents.AdminEmailTemplatesPage)(),
                loader: adminLoaders.adminEmailTemplatesLoader
            },
            {
                path: 'blog',
                element: withSuspense(AdminComponents.AdminBlogPage)(),
                loader: adminLoaders.adminBlogArticlesLoader,
                action: lazyActions.deleteArticleAction
            },
            {
                path: 'blog/dodaj',
                element: withSuspense(AdminComponents.AdminCreateOrEditBlogArticlePage)({actionType: "CREATE"}),
                loader: adminLoaders.loadArticleToEdit,
                action: adminLoaders.createOrEditBlogArticleAction
            },
            {
                path: 'blog/edytuj/:uuid',
                element: withSuspense(AdminComponents.AdminCreateOrEditBlogArticlePage)({actionType: "EDIT"}),
                loader: adminLoaders.loadArticleToEdit,
                action: adminLoaders.createOrEditBlogArticleAction
            },
            {
                path: 'feedback',
                element: withSuspense(AdminComponents.AdminFeedbackPage)(),
                loader: adminLoaders.feedbackLoader
            },
            {
                path: 'diagnostyka',
                element: withSuspense(AdminComponents.AdminDiagnosticPage)(),
                loader: adminLoaders.diagnosticsLoader
            },
            {
                path: 'traces',
                element: withSuspense(AdminComponents.AdminTracesPage)(),
                loader: adminLoaders.adminTracesPageLoader
            },
            {
                path: 'ked',
                element: withSuspense(AdminComponents.AdminKedRedirect)()
            },
            {
                path: 'ked/eksperymenty',
                element: withSuspense(AdminComponents.AdminKedExperimentPage)(),
                loader: adminLoaders.adminKedExperimentResultsLoader
            },
            {
                path: 'ked/oferta',
                element: withSuspense(AdminComponents.AdminKedOfferPage)(),
                action: adminLoaders.adminKedOfferAction,
            },
            {
                path: 'ked/spotkania',
                element: withSuspense(AdminComponents.AdminKedMeetingsPage)(),
                loader: adminLoaders.adminKedMeetingsLoader,
                action: adminLoaders.adminKedMeetingsAction
            },
            {
                path: 'ked/spotkania/nowe',
                element: withSuspense(AdminComponents.AdminCreateOrEditKedMeetingPage)({actionType: "CREATE"}),
                loader: adminLoaders.adminCreateKedMeetingLoader,
                action: adminLoaders.adminCreateOrEditKedMeetingAction
            },
            {
                path: 'ked/spotkania/:id',
                element: withSuspense(AdminComponents.AdminCreateOrEditKedMeetingPage)({actionType: "EDIT"}),
                loader: adminLoaders.adminEditKedMeetingLoader,
                action: adminLoaders.adminCreateOrEditKedMeetingAction
            },
            {
                path: 'ked/kursanci',
                element: withSuspense(AdminComponents.AdminCourseUsersPage)({course: AvailableCourses.KED}),
                loader: adminLoaders.adminKedUsersLoader
            },
            {
                path: 'ked/kursanci-ked-v2',
                element: withSuspense(AdminComponents.AdminCourseUsersPage)({course: AvailableCourses.KED_V2}),
                loader: adminLoaders.adminKedV2UsersLoader
            },
            {
                path: 'ked/kursanci-ped',
                element: withSuspense(AdminComponents.AdminCourseUsersPage)({course: AvailableCourses.PED}),
                loader: adminLoaders.adminPedUsersLoader
            },
            {
                path: 'ked/kursanci-zn',
                element: withSuspense(AdminComponents.AdminCourseUsersPage)({course: AvailableCourses.ZN}),
                loader: adminLoaders.adminZnUsersLoader
            },
            {
                path: 'ked/kursanci-spc',
                element: withSuspense(AdminComponents.AdminCourseUsersPage)({course: AvailableCourses.SPC}),
                loader: adminLoaders.adminSpcUsersLoader
            },
            {
                path: 'ked/weryfikacja',
                element: withSuspense(AdminComponents.AdminKedVerificationPage)(),
                action: adminLoaders.verifyKedAction
            },
            {
                path: 'ked/crm',
                element: withSuspense(AdminComponents.AdminKedCrmPage)(),
                loader: adminLoaders.adminKedCrmLoader
            },
            {
                path: 'ked/reklamy',
                element: withSuspense(AdminComponents.AdminKedAdsResultsPage)(),
                loader: redirectIfNotFullAdmin
            },
            {
                path: 'webinar-chat',
                element: withSuspense(AdminComponents.AdminWebinarChatPage)(),
                loader: adminLoaders.adminWebinarChatLoader
            },
            {
                path: 'transakcje',
                element: withSuspense(AdminComponents.AdminTransactionsPage)(),
                loader: adminLoaders.adminTransactionsLoader
            },
            {
                path: 'polecenia',
                element: withSuspense(AdminComponents.AdminReferralsPage)(),
                loader: adminLoaders.adminReferralsLoader
            },
            {
                path: 'analiza-metryk',
                element: withSuspense(AdminComponents.AdminMetricsAnalysisPage)(),
                loader: adminLoaders.adminMetricsAnalysisLoader,
                action: adminLoaders.adminMetricsAnalysisAction
            },
            {
                path: 'gamifikacja-kursu',
                element: withSuspense(AdminComponents.AdminCourseGamificationConfigPage)(),
                loader: adminLoaders.adminCourseGamificationConfigLoader,
                action: adminLoaders.adminCourseGamificationConfigAction
            },
        ]
    },
    {
        path: 'qr',
        id: 'qrRoot',
        children: [
            {
                index: true,
                element: <RedirectComponent to='/webinar' from='qr'/>
            }
        ]
    },
    {
        path: '/szkolenie',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        id: 'szkolenieRoot',
        loader: funnelLoaders.sessionIdLoader,
        children: [
            {
                index: true,
                element: withSuspense(FunnelComponents.WebinarOptInPage)({removeJit: true}),
                action: lazyActions.funnelKedWebinarOptInAction
            }
        ]
    },
    {
        path: '/webinar',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        loader: funnelLoaders.sessionIdLoader,
        id: 'webinarRoot',
        children: [
            {
                index: true,
                element: withSuspense(FunnelComponents.WebinarOptInPage)({removeJit: false}),
                action: lazyActions.funnelKedWebinarOptInAction
            },
            {
                path: 'v1',
                element: withSuspense(FunnelComponents.WebinarOptInV1Page)({removeJit: false}),
                action: lazyActions.funnelKedWebinarOptInV1Action
            },
            {
                path: 'v2',
                element: withSuspense(FunnelComponents.WebinarOptInV2Page)({removeJit: false}),
                action: lazyActions.funnelKedWebinarOptInV2Action
            },
            {
                path: 'v3',
                element: withSuspense(FunnelComponents.WebinarOptInPage)({removeJit: false}),
                action: lazyActions.funnelKedWebinarOptInV3Action
            },
            {
                path: 'potwierdzenie/:ksuid',
                element: withSuspense(FunnelComponents.WebinarOptInConfirmationPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.saveWebinarQuizResultsOrSchedulePhoneNotificationAction
            },
            {
                path: 'potwierdzenie-jit/:ksuid',
                element: withSuspense(FunnelComponents.WebinarOptInJustInTimeConfirmationPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.saveWebinarQuizResultsAction
            },
            {
                // Shared og-preview templates for /webinar/zakup{,-raty}/:ksuid
                // (lambda@edge normalizes dynamic ksuids onto these literal paths).
                path: 'zakup/_template',
                element: withSuspense(FunnelComponents.WebinarKedOgTemplatePage)(),
            },
            {
                path: 'zakup-raty/_template',
                element: withSuspense(FunnelComponents.WebinarKedOgTemplatePage)(),
            },
            {
                path: 'zakup-discounted/_template',
                element: withSuspense(FunnelComponents.WebinarKedOgTemplatePage)(),
            },
            {
                path: 'zakup/:ksuid',
                element: withSuspense(FunnelComponents.WebinarKedPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseWebinarAction
            },
            {
                path: 'zakup-raty/:ksuid',
                element: withSuspense(FunnelComponents.WebinarKedInstallmentPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseWebinarInstallmentAction
            },
            {
                path: 'zakup/sukces/:ksuid',
                element: withSuspense(FunnelComponents.WebinarKedSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'zakup/blad/:ksuid',
                element: withSuspense(FunnelComponents.WebinarKedUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'slajdy',
                element: withSuspense(FunnelComponents.WebinarSlides)(),
            },
            // {
            //     path: 'chat-preview-test',
            //     element: withSuspense(FunnelComponents.ChatPreviewTestPage)(),
            // },
            {
                path: 'powtorka/:ksuid',
                element: withSuspense(FunnelComponents.WebinarReplayPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseWebinarAction
            },
            {
                path: 'powtorka-szkolenia/:ksuid',
                element: withSuspense(FunnelComponents.WebinarFullReplayPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseWebinarAction
            },
            {
                // Bare replay route with NO ksuid is meaningless — a replay needs a webinar
                // record (resume position, offer state, recording deadline). Nothing links here
                // (all email/automation links carry the ksuid via createWebinarFullReplayUrlPlaceholder);
                // it was only reachable by manually stripping the ksuid, and rendered a stale
                // hardcoded 21-01 recording + old title/offer. Redirect to the evergreen offer page.
                path: 'powtorka-szkolenia',
                element: <Navigate to="/oferta/kurs-efektywnego-dzialania" replace/>,
            },
            {
                path: ':ksuid',
                element: withSuspense(FunnelComponents.WebinarPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseWebinarAction
            },
            {
                path: 'kurs-efektywnego-dzialania',
                element: withSuspense(FunnelComponents.WebinarKedPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseKedFromWebinarSalesPageAction
            },
            {
                path: 'kurs-efektywnego-dzialania/sukces',
                element: withSuspense(FunnelComponents.WebinarKedSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'kurs-efektywnego-dzialania/blad',
                element: withSuspense(FunnelComponents.WebinarKedUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
        ]
    },
    {
        path: '/en/webinar',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        loader: funnelLoaders.sessionIdLoader,
        id: 'enWebinarRoot',
        children: [
            {
                index: true,
                element: withSuspense(FunnelComponents.EnWebinarOptInPage)(),
                action: lazyActions.funnelEnWebinarOptInAction
            },
            {
                path: 'confirmation/:ksuid',
                element: withSuspense(FunnelComponents.EnWebinarOptInConfirmationPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.saveEnWebinarQuizResultsAction
            },
            {
                path: 'confirmation-jit/:ksuid',
                element: withSuspense(FunnelComponents.EnWebinarOptInJustInTimeConfirmationPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.saveEnWebinarJitQuizResultsAction
            },
            {
                path: 'purchase/:ksuid',
                element: withSuspense(FunnelComponents.EnWebinarPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseEnWebinarAction
            },
            {
                path: 'purchase/success/:ksuid',
                element: withSuspense(FunnelComponents.EnWebinarSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'purchase/error/:ksuid',
                element: withSuspense(FunnelComponents.EnWebinarUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: ':ksuid',
                element: withSuspense(FunnelComponents.EnWebinarPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseEnWebinarAction
            },
        ]
    },
    {
        path: '/en/privacy-policy',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        children: [
            {
                index: true,
                element: withSuspense(ContentComponents.EnPrivacyPolicyPage)(),
            },
        ]
    },
    {
        path: '/en/terms-of-service',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        children: [
            {
                index: true,
                element: withSuspense(ContentComponents.EnTermsOfServicePage)(),
            },
        ]
    },
    {
        path: '/slides',
        element: <RedirectComponent to='/en/slides'/>
    },
    {
        path: '/en/slides',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        children: [
            {
                index: true,
                element: withSuspense(ContentComponents.EnSlidesPage)(),
            },
        ]
    },
    {
        path: '/en/unsubscribe',
        element: withSuspense(ContentComponents.EnCancelNewsletter)(),
        errorElement: <Error404/>,
        loader: authLoaders.enCancelNewsletterLoader,
        action: lazyActions.cancelNewsletterFormAction
    },
    {
        path: '/newsletter',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        id: 'newsletterRoot',
        loader: funnelLoaders.sessionIdLoader,
        children: [
            {
                path: 'kurs-efektywnego-dzialania',
                element: withSuspense(FunnelComponents.WebinarKedPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseKedFromNewsletterAction
            },
            {
                // Dzień Dziecka gift sales page (annual, deadline 30 May). Standalone — no webinarDetails loader.
                path: 'dzien-dziecka',
                element: withSuspense(FunnelComponents.NewsletterDzienDzieckaPurchasePage)(),
                action: lazyActions.purchaseDzienDzieckaAction
            },
            {
                path: 'zakup/kurs-efektywnego-dzialania',
                element: withSuspense(FunnelComponents.NewsletterKedPurchasePage)(),
                action: lazyActions.purchaseNewsletterKedAction
            },
            {
                path: 'zakup/kurs-efektywnego-dzialania/:ksuid',
                element: withSuspense(FunnelComponents.NewsletterKedPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseNewsletterKedAction
            },
            {
                path: 'zakup/kurs-efektywnego-dzialania/sukces',
                element: withSuspense(FunnelComponents.NewsletterKedSuccessfulPurchasePage)()
            },
            {
                path: 'zakup/kurs-efektywnego-dzialania/blad',
                element: withSuspense(FunnelComponents.NewsletterKedUnsuccessfulPurchasePage)()
            },
            {
                // Dzień Dziecka 2026 — reuse the same Tpay success/error pages.
                // The gift-specific „odpisz z dedykacją" follow-up is in the confirmation email,
                // so the on-page success copy doesn't need to differ.
                path: 'zakup/dzien-dziecka/sukces',
                element: withSuspense(FunnelComponents.NewsletterKedSuccessfulPurchasePage)()
            },
            {
                path: 'zakup/dzien-dziecka/blad',
                element: withSuspense(FunnelComponents.NewsletterKedUnsuccessfulPurchasePage)()
            },
            {
                path: 'majowa-promocja',
                element: withSuspense(FunnelComponents.NewsletterKedReofferPage)(),
                action: lazyActions.purchaseNewsletterKedReofferAction
            },
            {
                path: 'majowa-promocja/sukces',
                element: withSuspense(FunnelComponents.NewsletterKedSuccessfulPurchasePage)()
            },
            {
                path: 'majowa-promocja/blad',
                element: withSuspense(FunnelComponents.NewsletterKedUnsuccessfulPurchasePage)()
            },
            {
                path: 'kurs-efektywnego-dzialania-v1',
                element: withSuspense(FunnelComponents.KedV1PurchasePage)(),
                action: lazyActions.kedV1PurchaseAction
            },
            {
                path: 'kurs-efektywnego-dzialania/sukces',
                element: withSuspense(FunnelComponents.WebinarKedSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'kurs-efektywnego-dzialania/blad',
                element: withSuspense(FunnelComponents.WebinarKedUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'podstawy-efektywnego-dzialania',
                element: withSuspense(FunnelComponents.PedPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchasePedFromSalesPageAction
            },
            {
                path: 'podstawy-efektywnego-dzialania/sukces',
                element: withSuspense(FunnelComponents.WebinarKedSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'podstawy-efektywnego-dzialania/blad',
                element: withSuspense(FunnelComponents.WebinarKedUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'koniec-roku-szkolnego',
                element: <RedirectComponent to='/oferta/kurs-efektywnego-dzialania'/>,

                // element: <RedirectComponent to='/newsletter/poczatek-roku-szkolnego'/>,
            },
            {
                path: 'poczatek-roku-szkolnego',
                element: <RedirectComponent to='/oferta/kurs-efektywnego-dzialania'/>,
                // element: <WebinarKedDiscountedPurchasePage/>,
                // loader: webinarDetailsLoader,
                // action: purchaseKedDiscountedAction
            },
            {
                path: 'poczatek-roku-szkolnego/sukces',
                element: <RedirectComponent to='/oferta/kurs-efektywnego-dzialania'/>,
                // element: <WebinarKedSuccessfulPurchasePage/>,
                // loader: webinarDetailsLoader,
            },
            {
                path: 'poczatek-roku-szkolnego/blad',
                element: <RedirectComponent to='/oferta/kurs-efektywnego-dzialania'/>,
                // element: <WebinarKedUnsuccessfulPurchasePage/>,
                // loader: webinarDetailsLoader,
            },
            {
                path: 'kurs-efektywnego-dzialania-v2',
                element: <RedirectComponent to='/oferta/kurs-efektywnego-dzialania'/>,
            },
            {
                path: 'cyber-monday',
                element: withSuspense(FunnelComponents.BlackFridayPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseBlackFridayAction
            },
            {
                path: 'cyber-monday/sukces',
                element: withSuspense(FunnelComponents.WebinarKedSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'cyber-monday/blad',
                element: withSuspense(FunnelComponents.WebinarKedUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'swieta',
                element: withSuspense(FunnelComponents.ChristmasPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseChristmasAction
            },
            {
                path: 'swieta/sukces',
                element: withSuspense(FunnelComponents.WebinarKedSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'swieta/blad',
                element: withSuspense(FunnelComponents.WebinarKedUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'organizacja-i-systematycznosc',
                element: withSuspense(ContentComponents.KedFollowUpOrganizationVideoPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'koncentracja',
                element: withSuspense(ContentComponents.KedFollowUpFocusVideoPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'motywacja',
                element: withSuspense(ContentComponents.KedFollowUpMotivationVideoPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'krzysiek',
                element: withSuspense(ContentComponents.KedFollowUpKrzysieckCaseStudyVideoPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'roznice-w-motywacjach',
                element: withSuspense(ContentComponents.MotivationDifferencesPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'optymalizacja-telefonu',
                element: withSuspense(ContentComponents.PhoneOptimizationPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'krotka-prezentacja-ked-v2',
                element: withSuspense(ContentComponents.KrotkaPrezentacjaKedV2Page)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'prezentacja-ked-v2-dla-uczniow',
                element: withSuspense(ContentComponents.ShortPresentationForChildrenKedV2Page)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'webinar-wiktor',
                element: withSuspense(ContentComponents.WebinarWiktorCaseStudyPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'webinar-stan-psychiczny-mlodziezy',
                element: withSuspense(ContentComponents.WebinarStanPsychicznyMlodziezyPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'webinar-efektywna-nauka',
                element: withSuspense(ContentComponents.WebinarEfektywnaNaukaPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
        ]
    },
    // SMS short links - redirect to /newsletter/* with experiment tracking
    {
        path: '/organizacja',
        element: <RedirectComponent
            to='/newsletter/organizacja-i-systematycznosc'
            from='sms'
            onRedirectAsync={() => TraceService.addTraceWithExperiment(TraceType.SmsLinkClick, SMS_CONTENT_EXPERIMENT, 'organizacja', 'sms')}
        />
    },
    {
        path: '/koncentracja',
        element: <RedirectComponent
            to='/newsletter/koncentracja'
            from='sms'
            onRedirectAsync={() => TraceService.addTraceWithExperiment(TraceType.SmsLinkClick, SMS_CONTENT_EXPERIMENT, 'koncentracja', 'sms')}
        />
    },
    {
        path: '/motywacja',
        element: <RedirectComponent
            to='/newsletter/motywacja'
            from='sms'
            onRedirectAsync={() => TraceService.addTraceWithExperiment(TraceType.SmsLinkClick, SMS_CONTENT_EXPERIMENT, 'motywacja', 'sms')}
        />
    },
    {
        path: '/rads',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        id: 'radsRoot',
        loader: funnelLoaders.sessionIdLoader,
        children: [
            {
                path: 'opinie',
                element: withSuspense(FunnelComponents.RadsReviewsPage)()
            },
            {
                path: 'kurs-efektywnego-dzialania',
                element: withSuspense(FunnelComponents.WebinarKedPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseKedFromRadsAction
            },
            {
                path: 'kurs-efektywnego-dzialania/sukces',
                element: withSuspense(FunnelComponents.WebinarKedSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'kurs-efektywnego-dzialania/blad',
                element: withSuspense(FunnelComponents.WebinarKedUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
        ]
    },
    {
        path: '/mistrzowskie-wsparcie',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        id: 'extraordinarySupportRoot',
        loader: funnelLoaders.sessionIdLoader,
        children: [
            // {
            //     index: true,
            //     element: <FunnelMWParentOptInPage/>,
            //     action: funnelMWParentOptInAction
            // },
            // {
            //     path: 'spotkanie',
            //     element: <FunnelMWParentVSLPage/>,
            // },
            // {
            //     path: 'spotkanie/umow',
            //     element: <FunnelMWParentCalendlyPage/>,
            // },
            // {
            //     path: 'uczen',
            //     element: <ExtraordinarySupportStudentPage/>
            // },
            // {
            //     path: 'start',
            //     element: <ParentOrStudentSelectPage/>
            // },
            {
                index: true,
                element: <RedirectComponent to='/webinar'/>,
                action: lazyActions.funnelMWParentOptInAction
            },
            {
                path: 'spotkanie',
                element: <RedirectComponent to='/webinar'/>,
            },
            {
                path: 'spotkanie/umow',
                element: <RedirectComponent to='/webinar'/>,
            },
            {
                path: 'uczen',
                element: <RedirectComponent to='/webinar'/>
            },
            {
                path: 'start',
                element: <RedirectComponent to='/webinar'/>
            }
        ]
    },
    {
        path: '/polecenie',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        id: 'polecenieRoot',
        loader: funnelLoaders.sessionIdLoader,
        children: [
            {
                path: 'kurs-efektywnego-dzialania',
                element: <ReferralKedPage persona="parent"/>
            },
            {
                path: 'kurs-efektywnego-dzialania/uczen',
                element: <ReferralKedPage persona="teen"/>
            },
            {
                path: 'kurs-efektywnego-dzialania/sukces',
                element: withSuspense(FunnelComponents.NewsletterKedSuccessfulPurchasePage)()
            },
            {
                path: 'kurs-efektywnego-dzialania/blad',
                element: withSuspense(FunnelComponents.NewsletterKedUnsuccessfulPurchasePage)()
            },
            {
                path: 'platforma',
                element: <ReferralPlatformPage/>
            },
        ]
    },
    {
        path: '/produkt',
        element: <RootLayout customHeader={<ProductsHeader loaderId='productRoot'/>} customFooter={<ProductsFooter/>}/>,
        errorElement: <Error404/>,
        loader: productLoaders.productRootLoader,
        shouldRevalidate: ({currentUrl, nextUrl, formMethod, defaultShouldRevalidate}) => {
            if (formMethod && formMethod !== 'GET') return true;
            if (currentUrl.pathname === nextUrl.pathname) return false;
            return defaultShouldRevalidate;
        },
        id: 'productRoot',
        children: [
            {
                index: true,
                element: withSuspenseGradient(ProductComponents.StudentDashboardPage)(),
                loader: productLoaders.studentDashboardLoader
            },
            {
                path: 'kurs-efektywnego-dzialania',
                element: withSuspenseGradient(ProductComponents.LearningCourseDashboardPage)({course: CourseDetails.KED}),
                loader: productLoaders.kedLoader,
                action: lazyActions.completeEpisodeAction
            },
            {
                path: 'kurs-efektywnego-dzialania-v2',
                element: withSuspenseGradient(ProductComponents.LearningCourseDashboardPage)({course: CourseDetails.KED_V2}),
                loader: productLoaders.kedV2Loader,
                action: lazyActions.completeEpisodeAction
            },
            {
                path: 'kurs-efektywnego-dzialania-v2/ranking',
                element: withSuspenseGradient(ProductComponents.CourseRankingsPage)(),
                loader: productLoaders.kedV2RankingsLoader
            },
            {
                path: 'podstawy-efektywnego-dzialania',
                element: withSuspenseGradient(ProductComponents.LearningCourseDashboardPage)({course: CourseDetails.PED}),
                loader: productLoaders.pedLoader,
                action: lazyActions.completeEpisodeAction
            },
            {
                path: 'zrozumiec-nastolatka',
                element: withSuspenseGradient(ProductComponents.LearningCourseDashboardPage)({course: CourseDetails.ZN}),
                loader: productLoaders.znLoader,
                action: lazyActions.completeEpisodeAction
            },
            {
                path: 'system-planowania-czasu',
                element: withSuspenseGradient(ProductComponents.LearningCourseDashboardPage)({course: CourseDetails.SPC}),
                loader: productLoaders.spcLoader,
                action: lazyActions.completeEpisodeAction
            },
            {
                path: 'kurs-efektywnego-dzialania/spotkanie/dziekuje',
                element: withSuspenseGradient(ContentComponents.RedirectAfterSchedulingIndividualKEDSessionPage)(),
            },
            {
                path: 'chat',
                element: withSuspenseGradient(ProductComponents.ChatBot)(),
                loader: contentLoaders.threadsLoader
            },
            {
                path: 'chat/zakup',
                element: withSuspenseGradient(ProductComponents.ChatPurchaseTokensInProducts)(),
                loader: contentLoaders.isLoggedInLoader,
                action: lazyActions.purchaseTokensAction
            },
            {
                path: 'spolecznosc',
                element: withSuspenseGradient(ProductComponents.CommunityPage)(),
                loader: productLoaders.communityPageLoader
            },
            {
                path: 'spolecznosc/spotkania',
                element: withSuspenseGradient(ProductComponents.CommunityWeeklyMeetingPage)(),
                loader: productLoaders.communityWeeklyMeetingsLoader
            },
            {
                path: 'spolecznosc/nagrania',
                element: withSuspenseGradient(ProductComponents.CommunityKedMeetingRecordingsPage)(),
                loader: productLoaders.communityKedMeetingRecordingsLoader
            },
            {
                path: 'spolecznosc/nagrania/:id',
                element: withSuspenseGradient(ProductComponents.CommunityKedMeetingRecordingDetailPage)(),
                loader: productLoaders.communityKedMeetingRecordingDetailLoader
            },
            {
                path: 'spolecznosc/spotkanie-wprowadzajace',
                element: withSuspenseGradient(ProductComponents.CommunityIntroMeetingPage)(),
                loader: productLoaders.communityIntroMeetingLoader
            },
            {
                path: 'zakup/kurs-efektywnego-dzialania',
                element: withSuspenseGradient(ProductComponents.KedEnrollPage)(),
                loader: redirectIfNotLoggedIn,
                action: lazyActions.purchaseKedFromPedCourseSalesPage
            },
            {
                path: 'zakup/kurs-efektywnego-dzialania/blad',
                element: withSuspenseGradient(FunnelComponents.WebinarKedUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'fiszki',
                element: withSuspenseGradient(ProductComponents.FlashcardsPage)(),
                loader: productLoaders.flashcardsLoader,
                action: productLoaders.flashcardsAction
            },
            {
                path: 'panel-rodzica',
                element: withSuspenseGradient(ProductComponents.ParentManagementPage)(),
                loader: productLoaders.parentManagementLoader
            },
            {
                path: 'panel-rodzica/sukces',
                element: withSuspenseGradient(ProductComponents.ParentPanelSuccessPage)()
            },
            {
                path: 'panel-rodzica/blad',
                element: withSuspenseGradient(ProductComponents.ParentPanelErrorPage)()
            },
            {
                path: 'polecenia',
                element: <ReferralsPage/>,
                loader: referralsLoader
            },
            {
                path: 'plan-dominacji',
                element: withSuspenseGradient(ProductComponents.PlannerPage)(),
                loader: productLoaders.plannerLoader
            },
            {
                path: 'planer-nauki',
                element: withSuspenseGradient(ProductComponents.MainLearningPlanPage)(),
                loader: productLoaders.mainLearningPlanLoader
            },
            {
                path: 'planer-nauki/:id',
                element: withSuspenseGradient(ProductComponents.LearningPlanPage)(),
                loader: productLoaders.learningPlanLoader,
                action: productLoaders.learningPlanAction
            },
            {
                path: 'planer-nauki/:planId/:dateKey',
                element: withSuspenseGradient(ProductComponents.LearningPlanExercisePage)(),
                loader: productLoaders.learningPlanExerciseLoader
            },
            {
                path: 'planer-nauki/:planId/:dateKey/:exerciseIndex',
                element: withSuspenseGradient(ProductComponents.LearningPlanExercisePage)(),
                loader: productLoaders.learningPlanExerciseLoader
            },
            {
                path: 'oferta/kurs-efektywnego-dzialania',
                element: withSuspenseGradient(FunnelComponents.LearningCourseEnrollPage)(),
                action: lazyActions.learningCourseEnrollAction
            },
            {
                path: 'zadania',
                element: withSuspenseGradient(ProductComponents.ExercisesPage)(),
                loader: productLoaders.exercisesLoader,
                action: productLoaders.exerciseAction
            },
            {
                path: 'zadania/:exerciseId',
                element: withSuspenseGradient(ProductComponents.ExerciseViewPage)(),
                loader: productLoaders.exerciseViewLoader,
                action: productLoaders.exerciseViewAction,
            },
            {
                path: 'zadania/dodaj',
                element: withSuspenseGradient(AdminComponents.AdminCreateOrEditExercisePage)({actionType: "CREATE"}),
                loader: adminLoaders.loadExerciseToEdit,
                action: adminLoaders.createOrEditExerciseAction
            },
            {
                path: 'zadania/edytuj/:uuid',
                element: withSuspenseGradient(AdminComponents.AdminCreateOrEditExercisePage)({actionType: "EDIT"}),
                loader: adminLoaders.loadExerciseToEdit,
                action: adminLoaders.createOrEditExerciseAction
            },
            {
                path: 'zadania/konfiguruj',
                element: withSuspenseGradient(AdminComponents.AdminSubjectConfigurationPage)(),
                loader: adminLoaders.adminSubjectConfigurationLoader,
                action: adminLoaders.adminSubjectConfigurationAction
            },
            // @Everyone
            {
                path: 'pro',
                element: withSuspenseGradient(ContentComponents.ProSubscriptionPage)(),
            },
        ]
    },
    {
        path: '/ustawienia',
        element: <RootLayout customHeader={<ProductsHeader loaderId='settingsRoot'/>}
                             customFooter={<ProductsFooter/>}/>,
        errorElement: <Error404/>,
        loader: productLoaders.productRootLoader,
        shouldRevalidate: ({currentUrl, nextUrl, formMethod, defaultShouldRevalidate}) => {
            if (formMethod && formMethod !== 'GET') return true;
            if (currentUrl.pathname === nextUrl.pathname) return false;
            return defaultShouldRevalidate;
        },
        id: 'settingsRoot',
        children: [
            // @Everyone (who is logged in)
            {
                index: true,
                element: withSuspenseGradient(ContentComponents.SettingsPage)(),
                loader: contentLoaders.settingsLoader,
                action: lazyActions.settingsAction
            },
            {
                path: 'ogolne',
                element: withSuspenseGradient(ContentComponents.SettingsPage)(),
                loader: contentLoaders.settingsLoader,
                action: lazyActions.settingsAction
            },
            {
                path: 'profil-edukacyjny',
                element: withSuspenseGradient(ContentComponents.SettingsPage)(),
                loader: contentLoaders.settingsLoader,
                action: lazyActions.settingsAction
            },
            {
                path: 'plan-dominacji',
                element: withSuspenseGradient(ContentComponents.SettingsPage)(),
                loader: contentLoaders.settingsLoader,
                action: lazyActions.settingsAction
            },
            {
                path: 'planer-nauki',
                element: withSuspenseGradient(ContentComponents.SettingsPage)(),
                loader: contentLoaders.settingsLoader,
                action: lazyActions.settingsAction
            },
            {
                path: 'powiadomienia',
                element: withSuspenseGradient(ContentComponents.SettingsPage)(),
                loader: contentLoaders.settingsLoader,
                action: lazyActions.settingsAction
            },
            {
                path: 'rozliczenia',
                element: withSuspenseGradient(ContentComponents.SettingsPage)(),
                loader: contentLoaders.settingsLoader,
                action: lazyActions.settingsAction
            },
        ]
    },
    {
        path: '/rejestracja-saas',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        loader: tokenLoader,
        id: 'rejestracjaSaasRoot',
        children: [
            {
                index: true,
                element: withSuspense(AuthComponents.RegisterSaas)(),
                loader: redirectIfLoggedIn,
                action: lazyActions.registerSaasAction
            }
        ]
    },
    {
        path: '/rejestracja',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        loader: tokenLoader,
        id: 'rejestracjaRoot',
        children: [
            {
                index: true,
                element: withSuspense(AuthComponents.RegisterSaas)(),
                loader: redirectIfLoggedIn,
                action: lazyActions.registerSaasAction
            }
        ]
    },
    {
        path: '/platforma',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        loader: tokenLoader,
        id: 'platformaRoot',
        children: [
            {
                index: true,
                // TODO: Change to SaasLandingPage over time.
                element: withSuspense(ContentComponents.PlatformStudentPresentationPage)(),
                loader: contentLoaders.isLoggedInLoader,
                action: lazyActions.launchOptInAction
            },
            {
                path: 'dziekuje-rodzic',
                element: withSuspense(ContentComponents.PlatformSoonThankYouParent)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'dziekuje-uczen',
                element: withSuspense(ContentComponents.PlatformSoonThankYouChildren)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'dostep',
                element: withSuspense(ContentComponents.PlatformStudentPresentationPage)()
            },
            {
                path: 'prezentacja',
                element: withSuspense(ContentComponents.PlatformPresentationPage)()
            },
            {
                path: 'prezentacja-uczen',
                element: withSuspense(ContentComponents.PlatformStudentPresentationPage)()
            },
            {
                path: 'prezentacja/blad',
                element: withSuspense(ContentComponents.PlatformPresentationUnsuccessfulPurchasePage)()
            },
            {
                path: 'prezentacja-uczen/blad',
                element: withSuspense(ContentComponents.PlatformStudentPresentationUnsuccessfulPurchasePage)()
            },
            {
                path: 'dziekuje',
                element: withSuspense(ContentComponents.PlatformSuccessfulPurchasePage)()
            },
            {
                path: 'blad',
                element: withSuspense(ContentComponents.PlatformUnsuccessfulPurchasePage)()
            },
        ]
    },
    {
        path: '/platforma-rodzic',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        loader: tokenLoader,
        id: 'platformaRodzicRoot',
        children: [
            {
                index: true,
                // TODO: Change to SaasLandingParentPage over time.
                element: withSuspense(ContentComponents.PlatformPresentationPage)(),
                loader: contentLoaders.isLoggedInLoader,
                action: lazyActions.launchOptInAction
            }
        ]
    },
    {
        path: '/system-planowania-czasu',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        children: [
            {
                index: true,
                element: withSuspense(FunnelComponents.SystemPlanowaniaCzasuPage)(),
                action: lazyActions.purchaseSpcAction
            },
            {
                path: 'dziekuje',
                element: withSuspense(FunnelComponents.SpcSuccessfulPurchasePage)(),
            },
            {
                path: 'blad',
                element: withSuspense(FunnelComponents.SpcUnsuccessfulPurchasePage)(),
            }
        ]
    },
    {
        path: '/film-wprowadzajacy',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        children: [
            {
                index: true,
                element: withSuspense(ContentComponents.FilmWprowadzajacyPage)(),
            }
        ]
    },
    {
        path: '/plc',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        id: 'plcContentRoot',
        children: [
            {
                index: true,
                element: withSuspense(ContentComponents.PrelaunchContentPage)(),
                loader: contentLoaders.prelaunchContentLoader,
                action: lazyActions.launchOptInAction
            },
            {
                path: ':videoId',
                element: withSuspense(ContentComponents.PrelaunchContentPage)(),
                loader: contentLoaders.prelaunchContentLoader,
                action: lazyActions.launchOptInAction
            },
            {
                path: 'rads',
                element: withSuspense(ContentComponents.PrelaunchContentPage)(),
                loader: contentLoaders.prelaunchContentLoader,
                action: lazyActions.launchOptInAction
            },
            {
                path: 'rads/:videoId',
                element: withSuspense(ContentComponents.PrelaunchContentPage)(),
                loader: contentLoaders.prelaunchContentLoader,
                action: lazyActions.launchOptInAction
            },
            {
                path: 'share',
                element: withSuspense(ContentComponents.PrelaunchContentPage)(),
                loader: contentLoaders.prelaunchContentLoader,
                action: lazyActions.launchOptInAction
            },
            {
                path: 'share/:videoId',
                element: withSuspense(ContentComponents.PrelaunchContentPage)(),
                loader: contentLoaders.prelaunchContentLoader,
                action: lazyActions.launchOptInAction
            }
        ]
    },
    {
        path: '/ebook',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        id: 'ebookRoot',
        children: [
            {
                index: true,
                element: withSuspense(ContentComponents.EbookLandingPage)(),
                loader: contentLoaders.isLoggedInLoader,
                action: lazyActions.launchOptInAction
            },
            {
                path: 'dziekuje',
                element: withSuspense(ContentComponents.EbookThankYouPage)(),
                loader: contentLoaders.isLoggedInLoader
            },
            {
                path: 'pobierz',
                element: <RedirectComponent
                    onRedirectAsync={() => {
                        return TraceService.addTraceWithExperiment(TraceType.EbookDownloaded, LAUNCH_LANDING_EXPERIMENT)
                    }}
                    to='https://produkacja.s3.eu-central-1.amazonaws.com/materialy/ebook-transformacja-nauki.pdf'/>
            },
            {
                path: 'webinar/pobierz',
                element: <RedirectComponent
                    onRedirectAsync={() => {
                        return TraceService.addTraceWithExperiment(TraceType.EbookDownloaded, DEFAULT_EXPERIMENT)
                    }}
                    to='https://produkacja.s3.eu-central-1.amazonaws.com/materialy/ebook-transformacja-nauki.pdf'/>
            },
            {
                path: 'plan-dominacji',
                element: <RedirectComponent
                    onRedirectAsync={() => {
                        return TraceService.addTraceWithExperiment(TraceType.PowerMapDownloaded, LAUNCH_LANDING_EXPERIMENT)
                    }}
                    to='https://docs.google.com/spreadsheets/d/1PPD1KpJW3GgoyVy_kIdVY1_tHTUziSizPXHUgZ210j4/edit?usp=sharing'/>
            },
        ]
    },
    {
        path: '/statystyki',
        element: <RootLayout customHeader={<ProductsHeader loaderId='statisticsRoot'/>}
                             customFooter={<ProductsFooter/>}/>,
        errorElement: <Error404/>,
        loader: productLoaders.productRootLoader,
        shouldRevalidate: ({currentUrl, nextUrl, formMethod, defaultShouldRevalidate}) => {
            if (formMethod && formMethod !== 'GET') return true;
            if (currentUrl.pathname === nextUrl.pathname) return false;
            return defaultShouldRevalidate;
        },
        id: 'statisticsRoot',
        children: [
            {
                index: true,
                element: withSuspenseGradient(ProductComponents.StatisticsPage)(),
                loader: productLoaders.statisticsLoader
            },
        ]
    },
    {
        path: '/ranking',
        element: <RootLayout customHeader={<ProductsHeader loaderId='rankingsRoot'/>}
                             customFooter={<ProductsFooter/>}/>,
        errorElement: <Error404/>,
        loader: productLoaders.productRootLoader,
        shouldRevalidate: ({currentUrl, nextUrl, formMethod, defaultShouldRevalidate}) => {
            if (formMethod && formMethod !== 'GET') return true;
            if (currentUrl.pathname === nextUrl.pathname) return false;
            return defaultShouldRevalidate;
        },
        id: 'rankingRoot',
        children: [
            {
                index: true,
                element: withSuspenseGradient(ProductComponents.RankingsPage)(),
                loader: productLoaders.rankingsLoader
            },
            {
                path: 'historia',
                element: withSuspenseGradient(ProductComponents.RankingHistoryPage)(),
                loader: productLoaders.rankingsHistoryLoader
            }
        ]
    },
    {
        path: '/',
        element: <RootLayout/>,
        errorElement: <Error404/>,
        loader: tokenLoader,
        shouldRevalidate: ({currentUrl, nextUrl, formMethod, defaultShouldRevalidate}) => {
            if (formMethod && formMethod !== 'GET') return true;
            if (currentUrl.pathname === nextUrl.pathname) return false;
            return defaultShouldRevalidate;
        },
        id: 'root',
        children: [
            // @Everyone
            {
                index: true,
                element: <IndexPage/>,
                loader: contentLoaders.randomBlogArticlesLoader
            },
            // @Everyone (who is not logged in)
            {
                path: 'logowanie',
                element: withSuspense(AuthComponents.Login)(),
                loader: redirectIfLoggedIn,
                action: lazyActions.loginAction
            },
            {
                path: 'weryfikuj-email/:code/:userId',
                element: withSuspense(AuthComponents.VerifyEmail)(),
                loader: authLoaders.verifyEmailLoader,
            },
            // @Everyone
            {
                path: 'logout',
                action: lazyActions.logoutAction,
                element: <RedirectComponent to='/'/>
            },
            // @Everyone (who is not logged in)
            {
                path: 'reset-hasla',
                loader: redirectIfLoggedIn,
                action: lazyActions.resetPasswordAction,
                element: withSuspense(AuthComponents.ResetPassword)()
            },
            // @Everyone (who is not logged in)
            {
                path: 'zmien-haslo/:uuid',
                loader: redirectIfLoggedIn,
                action: lazyActions.changePasswordAction,
                element: withSuspense(AuthComponents.ChangePassword)()
            },
            // @Everyone
            {
                path: '/aktywuj/:uuid',
                element: withSuspense(AuthComponents.ActivateAccount)(),
                loader: authLoaders.activateAccountLoader,
                action: lazyActions.activateAccountAction,
                shouldRevalidate: () => false
            },
            // @Everyone - Parent activation
            {
                path: '/aktywuj-konto-rodzica/:uuid',
                element: withSuspense(AuthComponents.ActivateParentAccount)(),
                loader: authLoaders.activateParentAccountLoader,
                action: lazyActions.activateParentAccountAction,
                shouldRevalidate: () => false
            },
            // @Everyone
            {
                path: 'kontakt',
                element: withSuspense(ContentComponents.Contact)(),
                action: lazyActions.contactAction
            },
            {
                path: '/potwierdz-newsletter',
                element: withSuspense(ContentComponents.ConfirmNewsletterPage)(),
            },
            {
                path: '/dziekuje',
                element: withSuspense(ContentComponents.ConfirmTransactionPage)(),
            },
            // @Everyone
            {
                path: 'oferta/chat',
                element: withSuspense(ContentComponents.ChatLandingPage)(),
                loader: contentLoaders.isLoggedInLoader,
                action: lazyActions.purchaseTokensAction
            },
            // @Everyone
            {
                path: 'chat',
                element: <RedirectComponent to={'/produkt/chat'}/>,
            },
            // @Everyone
            {
                path: 'oferta/kurs-efektywnego-dzialania',
                element: withSuspense(FunnelComponents.LearningCourseEnrollPage)(),
                action: lazyActions.learningCourseEnrollAction
            },
            // @Everyone — teen-targeted self-check page (no CTA, just an invitation to talk with parents)
            {
                path: 'kurs-efektywnego-dzialania/czy-to-dla-mnie',
                element: withSuspense(FunnelComponents.TeenSelfCheckPage)(),
            },
            // @Everyone - ZN course sales page for parents
            // {
            //     path: 'oferta/zrozumiec-nastolatka',
            //     element: withSuspense(FunnelComponents.ZnPurchasePage)(),
            //     loader: funnelLoaders.znPurchaseLoader,
            //     action: lazyActions.purchaseZnFromSalesPageAction
            // },
            // {
            //     path: 'oferta/zrozumiec-nastolatka/sukces',
            //     element: withSuspense(FunnelComponents.ZnPurchaseSuccessPage)()
            // },
            // {
            //     path: 'oferta/zrozumiec-nastolatka/blad',
            //     element: withSuspense(FunnelComponents.ZnPurchaseErrorPage)()
            // },
            // @Everyone, but not visible on the website before call
            {
                path: 'zakup/kurs-efektywnego-dzialania/:ksuid',
                element: withSuspense(FunnelComponents.LearningCoursePurchasePage)(),
                loader: funnelLoaders.individualKedOfferLoader,
                action: lazyActions.coursePurchaseAction
            },
            // @Everyone - used for redirect after successful purchase
            {
                path: 'zakup/kurs-efektywnego-dzialania/sukces',
                element: withSuspense(FunnelComponents.LearningCourseSuccessfulPurchasePage)(),
            },
            // @Everyone - used for redirect after successful purchase
            {
                path: 'zakup/kurs-efektywnego-dzialania/blad',
                element: withSuspense(FunnelComponents.LearningCourseUnsuccessfulPurchasePage)(),
            },
            // @Everyone, but not visible on the website before call
            {
                path: 'zakup/mistrzowskie-wsparcie',
                element: withSuspense(FunnelComponents.ExtraordinarySupportPurchasePage)(),
                loader: funnelLoaders.extraordinarySupportPurchaseLoader,
                action: lazyActions.extraordinarySupportPurchaseAction
            },
            // @Everyone, but not visible on the website before call
            {
                path: 'zakup/mistrzowskie-wsparcie/:ksuid',
                element: withSuspense(FunnelComponents.ExtraordinarySupportPurchasePage)(),
                loader: funnelLoaders.extraordinarySupportPurchaseLoader,
                action: lazyActions.extraordinarySupportPurchaseAction
            },
            // @Everyone - used for redirect after successful purchase
            {
                path: 'zakup/mistrzowskie-wsparcie/sukces',
                element: withSuspense(FunnelComponents.ExtraordinarySupportSuccessfulPurchasePage)()
            },
            // @Everyone - used for redirect after successful purchase
            {
                path: 'zakup/mistrzowskie-wsparcie/blad',
                element: withSuspense(FunnelComponents.ExtraordinarySupportUnsuccessfulPurchasePage)()
            },
            // @Everyone
            {
                path: 'oferta',
                element: withSuspense(ContentComponents.OfferLandingPage)(),
            },
            // @Everyone
            {
                path: 'o-nas',
                element: withSuspense(ContentComponents.AboutUsPage)(),
            },
            // @Everyone
            {
                path: 'oferty-pracy',
                element: withSuspense(ContentComponents.JobOffersPage)(),
            },
            // @Everyone
            {
                path: 'planer',
                element: withSuspense(ContentComponents.Planner)(),
                action: lazyActions.newsletterFormAction
            },
            // @Everyone
            {
                path: 'newsletter',
                action: lazyActions.newsletterFormAction
            },
            // @Everyone
            {
                path: 'anuluj-newsletter',
                element: withSuspense(ContentComponents.CancelNewsletter)(),
                loader: authLoaders.cancelNewsletterLoader,
                action: lazyActions.cancelNewsletterFormAction
            },
            // @Everyone — landing after clicking "stay subscribed" in re-engagement warning email
            {
                path: 'zostaje',
                element: withSuspense(ContentComponents.StaySubscribedPage)(),
            },
            // @Everyone
            {
                path: 'polityka-prywatnosci',
                element: withSuspense(ContentComponents.PrivacyPolicyPage)(),
            },
            // @Everyone
            {
                path: 'regulamin',
                element: withSuspense(ContentComponents.RegulationsPage)(),
            },
            // @Everyone
            {
                path: 'regulamin-ked',
                element: <RedirectComponent to='/regulaminy-szczegolowe-kursow'/>
            },
            {
                path: 'regulamin-mw',
                element: withSuspense(ContentComponents.RegulationsMWPage)(),
            },
            {
                path: 'regulamin-ped',
                element: <RedirectComponent to='/regulaminy-szczegolowe-kursow'/>,
            },
            {
                path: 'regulamin-zn',
                element: <RedirectComponent to='/regulaminy-szczegolowe-kursow'/>,
            },
            // @Everyone
            {
                path: 'regulaminy-szczegolowe-kursow',
                element: withSuspense(ContentComponents.DetailedCourseRegulationsPage)(),
            },
            // @Everyone
            {
                path: 'blog',
                element: withSuspense(ContentComponents.BlogPage)(),
                loader: contentLoaders.blogArticlesLoader,
                action: lazyActions.deleteArticleAction,
            },
            // @Everyone
            {
                path: 'blog/:uuid',
                element: withSuspense(ContentComponents.BlogPostPage)(),
                loader: contentLoaders.blogArticleLoader,
            },
            // @Everyone
            {
                path: 'opinie',
                element: withSuspense(ContentComponents.ReviewsPage)(),
            },
            // @Everyone
            {
                path: 'kurs-efektywnego-dzialania/rodzice',
                element: withSuspense(ContentComponents.ParentsPage)(),
            },
            // @Everyone — V3 onboarding flow (post-purchase parent start page)
            {
                path: 'start/:codeId',
                element: withSuspense(ContentComponents.ParentStartPage)(),
                loader: contentLoaders.parentStartLoader,
            },
            // @Everyone — child-side meeting page with embedded Calendly
            {
                path: 'spotkanie/:shortId',
                element: withSuspense(ContentComponents.ChildMeetingPage)(),
                loader: contentLoaders.childMeetingLoader,
            },
            // @Everyone
            {
                path: 'kurs-efektywnego-dzialania/prezentacja',
                element: withSuspense(ContentComponents.PresentationPage)(),
            },
            // @Everyone (who is logged in)
            {
                path: 'feedback',
                action: lazyActions.sendFeedbackAction
            },
            {
                path: 'feedbackCourse',
                action: lazyActions.mandatoryFeedbackCourseAction
            },
            {
                path: 'endCourseFeedback',
                action: lazyActions.mandatoryEndCourseFeedbackAction
            },
            // @Everyone
            {
                path: 'login-error',
                element: withSuspense(AuthComponents.LoginError)()
            },
            // @Everyone
            {
                path: 'rejestracja-udana',
                element: withSuspense(ContentComponents.RedirectAfterRegistrationPage)(),
            },
            {
                path: 'yt',
                element: <RedirectComponent to={'/webinar'} from='yt-bio'/>
            },
            {
                path: 'tiktok',
                element: <RedirectComponent to={'/webinar'} from='tiktok'/>
            },
            {
                path: 'ig',
                element: <RedirectComponent to={'/webinar'} from='ig'/>
            },
            {
                path: 'chronotypy',
                element: withSuspense(ContentComponents.SleepChronotypesTestPage)({cta: false})
            },
            {
                path: 'test-na-chronotyp',
                element: withSuspense(ContentComponents.SleepChronotypesTestPage)({cta: false})
            },
            {
                path: 'start',
                element: <RedirectComponent to='/webinar'/>
            },
            // TODO: Eventually get rid of this -> Maintained for backwards compatibility
            {
                path: '/aktywuj-ked/:uuid',
                element: withSuspense(AuthComponents.GrantProductAccessToStudentPage)(),
                loader: authLoaders.grantKedAccessToStudentLoader,
                action: lazyActions.grantKedAccessToStudentAction,
                shouldRevalidate: () => false,
            },
            // TODO: Eventually get rid of this -> Maintained for backwards compatibility [Removed 24.09.25]
            {
                path: '/aktywuj-kurs/:uuid',
                element: withSuspense(AuthComponents.GrantProductAccessToStudentPage)(),
                loader: authLoaders.grantKedAccessToStudentLoader,
                action: lazyActions.grantKedAccessToStudentAction,
            },
            {
                path: '/aktywuj-produkt/:uuid',
                element: withSuspense(AuthComponents.GrantProductAccessToStudentPage)(),
                loader: authLoaders.grantKedAccessToStudentLoader,
                action: lazyActions.grantKedAccessToStudentAction,
            },
            // Platform upsell page for WebinarV2WithZN codes
            {
                path: '/upsell/platforma/:code',
                element: withSuspense(FunnelComponents.PlatformUpsellPage)(),
                loader: funnelLoaders.platformUpsellLoader,
            },
            // Parent payment routes
            {
                path: '/r/:id',
                element: withSuspense(FunnelComponents.ParentPaymentPage)(),
                loader: funnelLoaders.parentPaymentLoader
            },
            {
                path: '/r/:id/sukces',
                element: withSuspense(FunnelComponents.ParentPaymentSuccessPage)()
            },
            {
                path: '/r/:id/blad',
                element: withSuspense(FunnelComponents.ParentPaymentErrorPage)()
            },
            // @Everyone
            {
                path: 'materialy',
                element: withSuspense(ContentComponents.MaterialsPage)()
            },
            // @Everyone
            {
                path: 'materiały',
                element: withSuspense(ContentComponents.MaterialsPage)()
            },
            {
                path: 'prezentacja',
                element: withSuspense(ContentComponents.MaterialsPageV2)()
            },
            // @Everyone
            {
                path: 'slajdy',
                element: withSuspense(ContentComponents.SlidesPage)()
            },
            // @Everyone
            {
                path: 'pro',
                element: withSuspense(ContentComponents.ProSubscriptionPage)(),
            },
            // @Everyone
            {
                path: 'platnosc/sukces',
                element: withSuspense(FunnelComponents.PaymentSuccessPage)()
            },
            // @Everyone
            {
                path: 'platnosc/blad',
                element: withSuspense(FunnelComponents.PaymentErrorPage)()
            },
            {
                path: 'spc',
                element: <RedirectComponent to={'/system-planowania-czasu'} from='sms'/>
            },
            {
                path: 'maj',
                element: <RedirectComponent to={'/newsletter/majowa-promocja'} from='sms'/>
            },
            {
                path: 'dzien-dziecka',
                element: <RedirectComponent to={'/newsletter/dzien-dziecka'} from='sms'/>
            }
        ]
    },
]);

function App() {
    // Clean up expired presigned URLs periodically
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            presignedUrlCache.cleanExpired();
        }, 60000); // Clean up every minute

        return () => {
            clearInterval(cleanupInterval);
        };
    }, []);

    return <HelmetProvider>
        <ToastProvider>
            <UserProvider>
                <ChunkErrorBoundary>
                    <RouterProvider router={router}/>
                </ChunkErrorBoundary>
            </UserProvider>
        </ToastProvider>
    </HelmetProvider>
}

export default App;

export type ActionResponse = {
    body: string
    status: number
}
